import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const PAGE_SIZE = 20;

function targetLabel(entry) {
    if (entry.targetType === 'ALL') return 'Todos';
    if (entry.targetType === 'AUTOMATIC') return `Automática (${entry.eventKey || '—'})`;
    return entry.targetUserName || entry.targetUserEmail || entry.targetUserId || 'Usuario';
}

export default function AdminNotificationsScreen() {
    const { authFetch } = useAuth();

    const [target, setTarget] = useState('ALL');
    const [selectedUser, setSelectedUser] = useState(null);
    const [userOptions, setUserOptions] = useState([]);
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const searchTimer = useRef(null);

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [sendSuccess, setSendSuccess] = useState(null);

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const loadLog = useCallback(
        async (offset = 0) => {
            setLoadError(null);
            setIsLoading(true);
            try {
                const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
                const result = await authFetch(`/admin/notifications?${params.toString()}`);
                if (offset === 0) setItems(result.items);
                else setItems((prev) => [...prev, ...result.items]);
                setTotal(result.total);
            } catch (err) {
                setLoadError(err instanceof ApiError ? err.message : 'No se pudo cargar el historial.');
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch]
    );

    useEffect(() => {
        loadLog(0);
    }, [loadLog]);

    const handleUserSearch = (query) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        if (!query.trim()) {
            setUserOptions([]);
            return;
        }
        searchTimer.current = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const params = new URLSearchParams({ q: query.trim(), limit: '10' });
                const result = await authFetch(`/admin/users?${params.toString()}`);
                setUserOptions(result.items);
            } catch {
                setUserOptions([]);
            } finally {
                setUserSearchLoading(false);
            }
        }, 300);
    };

    const handleSend = async () => {
        setSendError(null);
        setSendSuccess(null);
        if (!title.trim() || !body.trim()) {
            setSendError('El título y el mensaje son obligatorios.');
            return;
        }
        if (target === 'USER' && !selectedUser) {
            setSendError('Selecciona un usuario destinatario.');
            return;
        }
        if (!window.confirm(target === 'ALL' ? '¿Enviar esta notificación a TODOS los usuarios?' : `¿Enviar esta notificación a "${selectedUser.name || selectedUser.email}"?`)) {
            return;
        }

        setSending(true);
        try {
            await authFetch('/admin/notifications', {
                method: 'POST',
                body: {
                    target,
                    userId: target === 'USER' ? selectedUser.id : undefined,
                    title: title.trim(),
                    body: body.trim(),
                },
            });
            setSendSuccess('Notificación enviada.');
            setTitle('');
            setBody('');
            setSelectedUser(null);
            loadLog(0);
        } catch (err) {
            setSendError(err instanceof ApiError ? err.message : 'No se pudo enviar la notificación.');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Header title="Notificaciones" showSettings={false} />
            <AdminNav />
            <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
                <Card elevation={2} sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Enviar notificación</Typography>

                        {sendError && <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert>}
                        {sendSuccess && <Alert severity="success" sx={{ mb: 2 }}>{sendSuccess}</Alert>}

                        <Stack spacing={2}>
                            <TextField
                                select
                                label="Destinatario"
                                value={target}
                                onChange={(e) => {
                                    setTarget(e.target.value);
                                    setSelectedUser(null);
                                }}
                                size="small"
                                sx={{ maxWidth: 260 }}
                            >
                                <MenuItem value="ALL">Todos los usuarios</MenuItem>
                                <MenuItem value="USER">Un usuario concreto</MenuItem>
                            </TextField>

                            {target === 'USER' && (
                                <Autocomplete
                                    options={userOptions}
                                    value={selectedUser}
                                    onChange={(_e, value) => setSelectedUser(value)}
                                    onInputChange={(_e, value) => handleUserSearch(value)}
                                    getOptionLabel={(u) => (u ? `${u.name || 'Sin nombre'} · ${u.email}` : '')}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    loading={userSearchLoading}
                                    noOptionsText="Escribe para buscar por nombre o email"
                                    renderInput={(params) => <TextField {...params} label="Buscar usuario" size="small" />}
                                />
                            )}

                            <TextField
                                label="Título"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                size="small"
                                inputProps={{ maxLength: 100 }}
                                fullWidth
                            />
                            <TextField
                                label="Mensaje"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                size="small"
                                multiline
                                minRows={3}
                                inputProps={{ maxLength: 500 }}
                                fullWidth
                            />

                            <Box>
                                <Button variant="contained" disabled={sending} onClick={handleSend}>
                                    {sending ? 'Enviando…' : 'Enviar'}
                                </Button>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Typography variant="h6" sx={{ mb: 2 }}>Historial</Typography>

                {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

                <Stack spacing={2}>
                    {items.map((entry) => (
                        <Card key={entry.id} elevation={1}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography variant="subtitle2">{entry.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{entry.body}</Typography>
                                    </Box>
                                    <Chip size="small" label={targetLabel(entry)} color={entry.targetType === 'AUTOMATIC' ? 'default' : 'primary'} />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(entry.createdAt).toLocaleString()}
                                    {entry.targetType !== 'AUTOMATIC' && ` · ${entry.recipientCount} destinatario(s)`}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                    {!isLoading && items.length === 0 && (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin notificaciones enviadas.</Typography>
                    )}
                </Stack>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && items.length < total && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={() => loadLog(items.length)}>Cargar más ({items.length}/{total})</Button>
                    </Box>
                )}
            </Container>
        </>
    );
}
